const SECTION_ID_PREFIX = 'shopify-section-';

/** @type {Map<string, Promise<Document>>} */
const sectionDocumentPromises = new Map();

/** @type {Map<string, Promise<void>>} */
const submenuLoadPromises = new Map();

/**
 * @returns {URL}
 */
function getSectionFetchBaseUrl() {
  const rootPath = (typeof Shopify !== 'undefined' && Shopify.routes?.root) || '/';
  return new URL(rootPath, window.location.origin);
}

/**
 * @param {string} sectionId
 * @returns {string}
 */
function buildSectionsApiUrl(sectionId) {
  const url = getSectionFetchBaseUrl();
  url.searchParams.set('sections', normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}

/**
 * @param {Record<string, unknown>} data
 * @param {string} normalizedId
 * @returns {string}
 */
function extractSectionHtml(data, normalizedId) {
  const direct = data[normalizedId];
  if (typeof direct === 'string' && direct.length > 0) {
    return direct;
  }

  const prefixed = data[`${SECTION_ID_PREFIX}${normalizedId}`];
  if (typeof prefixed === 'string' && prefixed.length > 0) {
    return prefixed;
  }

  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== 'string' || value.length === 0) {
      continue;
    }

    if (key === normalizedId || key.endsWith(`__${normalizedId}`)) {
      return value;
    }
  }

  const fallbackKey = Object.keys(data)[0];
  if (fallbackKey && typeof data[fallbackKey] === 'string' && data[fallbackKey].length > 0) {
    return data[fallbackKey];
  }

  throw new Error('header-submenu-lazy: empty section response');
}

/**
 * @param {string} sectionId
 * @returns {string}
 */
function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * @param {ParentNode} root
 */
function activateScripts(root) {
  root.querySelectorAll('script').forEach((script) => {
    const clone = document.createElement('script');
    for (const { name, value } of script.attributes) {
      clone.setAttribute(name, value);
    }
    clone.textContent = script.textContent;
    script.replaceWith(clone);
  });
}

/**
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
async function fetchSectionHtml(sectionId) {
  const normalizedId = normalizeSectionId(sectionId);

  const response = await fetch(buildSectionsApiUrl(normalizedId), {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (!response.ok) {
    throw new Error(`Section HTTP ${response.status}`);
  }

  const data = await response.json();
  return extractSectionHtml(data, normalizedId);
}

/**
 * @param {string} sectionId
 * @returns {Promise<Document>}
 */
async function getSectionDocument(sectionId) {
  const normalizedId = normalizeSectionId(sectionId);
  let promise = sectionDocumentPromises.get(normalizedId);

  if (!promise) {
    promise = fetchSectionHtml(normalizedId)
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch((error) => {
        sectionDocumentPromises.delete(normalizedId);
        throw error;
      });
    sectionDocumentPromises.set(normalizedId, promise);
  }

  return promise;
}

/**
 * @param {string} sectionId
 */
function prefetchHeaderSection(sectionId) {
  if (!sectionId) {
    return;
  }

  void getSectionDocument(sectionId).catch(() => {});
}

/**
 * @param {Document} doc
 * @param {string} key
 * @returns {HTMLElement | null}
 */
function findSubmenuInDocument(doc, key) {
  const selector = `[data-submenu-key="${CSS.escape(key)}"]`;
  const candidates = doc.querySelectorAll(selector);

  for (const candidate of candidates) {
    if (!(candidate instanceof HTMLElement)) {
      continue;
    }

    if (candidate.children.length > 0 && !candidate.hasAttribute('data-lazy-submenu')) {
      return candidate;
    }
  }

  for (const candidate of candidates) {
    if (candidate instanceof HTMLElement && candidate.children.length > 0) {
      return candidate;
    }
  }

  const first = candidates[0];
  return first instanceof HTMLElement ? first : null;
}

/**
 * @param {HTMLElement} target
 * @param {HTMLElement} source
 */
function mountSubmenuContent(target, source) {
  target.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
  activateScripts(target);
  target.setAttribute('data-loaded', '');
  target.removeAttribute('data-lazy-submenu');
  target.setAttribute('aria-busy', 'false');

  const navItem = target.closest('.global-header__nav-item');
  if (
    navItem &&
    (navItem.matches(':hover') || navItem.matches(':focus-within') || navItem.classList.contains('global-header__nav-item--submenu-open'))
  ) {
    target.setAttribute('aria-hidden', 'false');
  }

  target.dispatchEvent(new CustomEvent('header-submenu:loaded', { bubbles: true }));
}

/**
 * @param {HTMLElement} submenu
 * @param {Document} doc
 */
function mountSubmenuFromDocument(submenu, doc) {
  const key = submenu.dataset.submenuKey ?? '';
  const remoteSource = findSubmenuInDocument(doc, key);

  if (!remoteSource || remoteSource.children.length === 0) {
    throw new Error(`header-submenu-lazy: submenu empty for key ${key}`);
  }

  mountSubmenuContent(submenu, remoteSource);
}

/**
 * Fetches header section HTML via Section Rendering API and injects the matching submenu.
 *
 * @param {HTMLElement} submenu
 * @returns {Promise<void>}
 */
function ensureSubmenuLoaded(submenu) {
  if (!(submenu instanceof HTMLElement)) {
    return Promise.resolve();
  }

  if (submenu.hasAttribute('data-loaded')) {
    return Promise.resolve();
  }

  const key = submenu.dataset.submenuKey ?? '';
  if (!key) {
    return Promise.resolve();
  }

  const existing = submenuLoadPromises.get(key);
  if (existing) {
    return existing;
  }

  submenu.setAttribute('aria-busy', 'true');

  const loadPromise = (async () => {
    const sectionId =
      submenu.dataset.sectionId ||
      submenu.closest('.global-header')?.getAttribute('data-section-id') ||
      '';

    if (!sectionId) {
      throw new Error('header-submenu-lazy: missing data-section-id');
    }

    const doc = await getSectionDocument(sectionId);
    mountSubmenuFromDocument(submenu, doc);
  })();

  submenuLoadPromises.set(key, loadPromise);

  return loadPromise.catch((error) => {
    submenuLoadPromises.delete(key);
    submenu.setAttribute('aria-busy', 'false');
    submenu.dataset.error = error instanceof Error ? error.message : String(error);
    submenu.dispatchEvent(
      new CustomEvent('header-submenu:error', { bubbles: true, detail: { error } })
    );
    throw error;
  });
}

/**
 * @param {Element} navItem
 */
function preloadSubmenuForNavItem(navItem) {
  const submenu = navItem.querySelector('.global-header__submenu[data-lazy-submenu]');
  if (submenu instanceof HTMLElement) {
    void ensureSubmenuLoaded(submenu);
  }
}

/**
 * @param {HTMLElement} header
 */
function scheduleHeaderSectionPrefetch(sectionId) {
  if (!sectionId) {
    return;
  }

  const runPrefetch = () => {
    prefetchHeaderSection(sectionId);
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(runPrefetch, { timeout: 2500 });
    return;
  }

  window.setTimeout(runPrefetch, 1500);
}

function initHeaderSubmenuLazy(header) {
  if (header.dataset.headerSubmenuLazyInit === 'true') {
    return;
  }

  header.dataset.headerSubmenuLazyInit = 'true';

  const sectionId = header.getAttribute('data-section-id') || '';

  const nav = header.querySelector('.global-header__nav');
  if (nav instanceof HTMLElement) {
    nav.addEventListener(
      'pointerenter',
      () => {
        scheduleHeaderSectionPrefetch(sectionId);
      },
      { once: true, passive: true }
    );
  }

  header.addEventListener(
    'pointerenter',
    (event) => {
      const navItem = event.target instanceof Element ? event.target.closest('.global-header__nav-item') : null;
      if (!navItem || !header.contains(navItem)) {
        return;
      }

      preloadSubmenuForNavItem(navItem);
    },
    true
  );

  header.addEventListener(
    'focusin',
    (event) => {
      const navItem = event.target instanceof Element ? event.target.closest('.global-header__nav-item') : null;
      if (!navItem || !header.contains(navItem)) {
        return;
      }

      preloadSubmenuForNavItem(navItem);
    },
    true
  );

  // The pointer or focus may already be on a menu item whose pointerenter or focusin came before
  // this file ran, and a pointer that stays on that item raises no new pointerenter: so that item's
  // submenu is loaded now, as its pointerenter would have done. With the theme setting "Load theme
  // scripts when first needed" on (the header carries data-header-first-use) this file loads on
  // first use (sections/header.liquid), after the first pointer movement; with it off this file is
  // a deferred script, which on a slow page also runs after a pointer can have reached the menu
  // (PG-C2-HOVER: in the desktop menu checks of 27 and 28 Oct, with the pointer resting on one menu
  // item, the menu stayed closed in the 3 runs whose card code was ready only after 13 s, with or
  // without "Swatch rows set up in short steps", and opened in all 11 runs where it was ready by 8 s).
  header
    .querySelectorAll('.global-header__nav-item:hover, .global-header__nav-item:focus-within')
    .forEach(preloadSubmenuForNavItem);
}

window.tjcHeaderSubmenuLazy = {
  ensure: ensureSubmenuLoaded,
  prefetch: prefetchHeaderSection,
};

document.querySelectorAll('.global-header[data-lazy-submenus="true"]').forEach((header) => {
  if (header instanceof HTMLElement) {
    initHeaderSubmenuLazy(header);
  }
});
